import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Search, MoreVertical, Power, PowerOff, Zap, Sparkles, User, Settings, Brain } from 'lucide-react'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { reconciliationApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ReconciliationRule, CreateRuleRequest, RuleCondition, RuleAction, RuleConditionField, RuleConditionOperator, RuleActionType, RuleOrigin } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'
import { useRuleSuggestionCount } from '@/components/reconciliation'

const CONDITION_FIELDS: { value: RuleConditionField; label: string }[] = [
  { value: 'description', label: 'Description' },
  { value: 'amount', label: 'Amount' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'type', label: 'Transaction Type' },
]

const CONDITION_OPERATORS: { value: RuleConditionOperator; label: string }[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
]

const ACTION_TYPES: { value: RuleActionType; label: string }[] = [
  { value: 'set_vendor', label: 'Set Vendor' },
  { value: 'set_category', label: 'Set Category' },
  { value: 'mark_reconciled', label: 'Mark as Reconciled' },
  { value: 'mark_transfer', label: 'Mark as Transfer' },
  { value: 'mark_recurring', label: 'Mark as Recurring' },
  { value: 'ignore', label: 'Ignore' },
]

export default function ReconciliationRulesPage() {
  const { canAddEdit } = usePermissions()
  const canEdit = canAddEdit('RECONCILIATION')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<ReconciliationRule | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [formData, setFormData] = useState<CreateRuleRequest>({
    rule_name: '',
    description: '',
    priority: 50,
    conditions: [{ field: 'description', operator: 'contains', value: '' }],
    actions: [{ action_type: 'set_vendor', value: '' }],
  })

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['reconciliation-rules'],
    queryFn: () => reconciliationApi.rules.list(),
  })

  const createMutation = useMutation({
    mutationFn: reconciliationApi.rules.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-rules'] })
      toast.success('Rule created successfully')
      handleCloseDialog()
    },
    onError: () => toast.error('Failed to create rule'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateRuleRequest }) =>
      reconciliationApi.rules.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-rules'] })
      toast.success('Rule updated successfully')
      handleCloseDialog()
    },
    onError: () => toast.error('Failed to update rule'),
  })

  const deleteMutation = useMutation({
    mutationFn: reconciliationApi.rules.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-rules'] })
      toast.success('Rule deleted successfully')
    },
    onError: () => toast.error('Failed to delete rule'),
  })

  const toggleMutation = useMutation({
    mutationFn: reconciliationApi.rules.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-rules'] })
      toast.success('Rule toggled successfully')
    },
    onError: () => toast.error('Failed to toggle rule'),
  })

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingRule(null)
    setFormData({
      rule_name: '',
      description: '',
      priority: 50,
      conditions: [{ field: 'description', operator: 'contains', value: '' }],
      actions: [{ action_type: 'set_vendor', value: '' }],
    })
  }

  const handleEdit = (rule: ReconciliationRule) => {
    setEditingRule(rule)
    setFormData({
      rule_name: rule.rule_name,
      description: rule.description || '',
      priority: rule.priority,
      conditions: rule.conditions,
      actions: rule.actions,
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingRule) {
      updateMutation.mutate({ id: editingRule.rule_id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const filteredRules = (data?.rules || []).filter(rule =>
    rule.rule_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rule.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const suggestionCount = useRuleSuggestionCount()

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reconciliation Rules</h1>
          <p className="text-muted-foreground">Automate transaction categorization with rules</p>
        </div>
        <div className="flex items-center gap-2">
          {suggestionCount > 0 && (
            <Link to="/reconciliation/rule-suggestions">
              <Button variant="outline">
                <Brain className="h-4 w-4 mr-2" />
                AI Suggestions
                <Badge variant="secondary" className="ml-2">{suggestionCount}</Badge>
              </Button>
            </Link>
          )}
          {canEdit && (
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Rule
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search rules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <RulesTable
            rules={filteredRules}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={(id) => deleteMutation.mutate(id)}
            onToggle={(id) => toggleMutation.mutate(id)}
            canAddEdit={canEdit}
          />
        </CardContent>
      </Card>

      <RuleDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        isEditing={!!editingRule}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}

interface RulesTableProps {
  rules: ReconciliationRule[]
  isLoading: boolean
  onEdit: (rule: ReconciliationRule) => void
  onDelete: (id: string) => void
  onToggle: (id: string) => void
  canAddEdit: boolean
}

function RulesTable({ rules, isLoading, onEdit, onDelete, onToggle, canAddEdit }: RulesTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Rule Name</TableHead>
          <TableHead>Origin</TableHead>
          <TableHead>Priority</TableHead>
          <TableHead>Conditions</TableHead>
          <TableHead>Actions</TableHead>
          <TableHead>Times Matched</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-[50px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
          </TableRow>
        ) : rules.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-8">No rules found</TableCell>
          </TableRow>
        ) : (
          rules.map((rule) => (
            <TableRow key={rule.rule_id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  <div>
                    <p className="font-medium">{rule.rule_name}</p>
                    {rule.description && (
                      <p className="text-sm text-muted-foreground truncate max-w-[200px]">{rule.description}</p>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <RuleOriginBadge origin={rule.rule_origin} />
              </TableCell>
              <TableCell>
                <Badge variant="outline">{rule.priority}</Badge>
              </TableCell>
              <TableCell>
                <Badge variant="secondary">{rule.conditions.length} condition(s)</Badge>
              </TableCell>
              <TableCell>
                <Badge variant="secondary">{rule.actions.length} action(s)</Badge>
              </TableCell>
              <TableCell>{rule.times_matched}</TableCell>
              <TableCell>
                <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                  {rule.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </TableCell>
              <TableCell>
                {canAddEdit && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(rule)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onToggle(rule.rule_id)}>
                        {rule.is_active ? (
                          <><PowerOff className="h-4 w-4 mr-2" />Disable</>
                        ) : (
                          <><Power className="h-4 w-4 mr-2" />Enable</>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => onDelete(rule.rule_id)}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  )
}

interface RuleDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (e: React.FormEvent) => void
  formData: CreateRuleRequest
  setFormData: React.Dispatch<React.SetStateAction<CreateRuleRequest>>
  isEditing: boolean
  isLoading: boolean
}

function RuleDialog({ isOpen, onClose, onSubmit, formData, setFormData, isEditing, isLoading }: RuleDialogProps) {
  const updateCondition = (index: number, field: keyof RuleCondition, value: string) => {
    const newConditions = [...formData.conditions]
    newConditions[index] = { ...newConditions[index], [field]: value }
    setFormData({ ...formData, conditions: newConditions })
  }

  const updateAction = (index: number, field: keyof RuleAction, value: string) => {
    const newActions = [...formData.actions]
    newActions[index] = { ...newActions[index], [field]: value }
    setFormData({ ...formData, actions: newActions })
  }

  const addCondition = () => {
    setFormData({
      ...formData,
      conditions: [...formData.conditions, { field: 'description', operator: 'contains', value: '' }],
    })
  }

  const addAction = () => {
    setFormData({
      ...formData,
      actions: [...formData.actions, { action_type: 'set_vendor', value: '' }],
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
          <DialogDescription>
            Define conditions and actions for automatic transaction processing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rule Name *</Label>
                <Input
                  value={formData.rule_name}
                  onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                  placeholder="e.g., Netflix Subscription"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Priority (1-100)</Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 50 })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
              />
            </div>

            {/* Conditions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Conditions</Label>
                <Button type="button" variant="outline" size="sm" onClick={addCondition}>
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              {formData.conditions.map((condition, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Select value={condition.field} onValueChange={(v) => updateCondition(index, 'field', v)}>
                    <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONDITION_FIELDS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={condition.operator} onValueChange={(v) => updateCondition(index, 'operator', v)}>
                    <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONDITION_OPERATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    className="flex-1"
                    value={condition.value as string}
                    onChange={(e) => updateCondition(index, 'value', e.target.value)}
                    placeholder="Value..."
                  />
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Actions</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAction}>
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              {formData.actions.map((action, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Select value={action.action_type} onValueChange={(v) => updateAction(index, 'action_type', v)}>
                    <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ACTION_TYPES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {['set_vendor', 'set_category', 'set_client'].includes(action.action_type) && (
                    <Input
                      className="flex-1"
                      value={(action.value as string) || ''}
                      onChange={(e) => updateAction(index, 'value', e.target.value)}
                      placeholder="Value..."
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// Rule Origin Badge Component
const RULE_ORIGIN_CONFIG: Record<RuleOrigin, { label: string; icon: React.ReactNode; variant: 'default' | 'secondary' | 'outline' }> = {
  USER: { label: 'User', icon: <User className="h-3 w-3" />, variant: 'outline' },
  AI_SUGGESTED: { label: 'AI', icon: <Sparkles className="h-3 w-3" />, variant: 'secondary' },
  AI_AUTO: { label: 'AI Auto', icon: <Brain className="h-3 w-3" />, variant: 'secondary' },
  SYSTEM: { label: 'System', icon: <Settings className="h-3 w-3" />, variant: 'default' },
}

function RuleOriginBadge({ origin }: { origin?: RuleOrigin }) {
  const config = RULE_ORIGIN_CONFIG[origin || 'USER']
  return (
    <Badge variant={config.variant} className="gap-1">
      {config.icon}
      {config.label}
    </Badge>
  )
}
